// The frame every public/auth screen shares (spec/13 §1): the eclipse mark and the wordmark above one
// clean card, centred in the nebula (BRAND.md §6.1, ADR-0060: a sign-in page is a screen nobody works on,
// so it may have the sky; the console's working screens never do, and in the light theme the sky paints
// nothing). Sign-in, sign-up and invitation-accept all render through this, so the organisation console
// (`app.`) and the HQ console (`hq.`) sign in on the same bar.

import { lazy, Suspense, type ReactNode } from 'react';

import { Mark } from '../shell/Mark';

export interface AuthLayoutProps {
  /** The `<h1>` inside the card. */
  readonly title: string;
  /** One muted line under the title, on the identify/form step. Absent on later steps. */
  readonly subtitle?: ReactNode;
  /** A muted "who this is for" note below the card. */
  readonly footer?: ReactNode;
  /** A multi-step flow's progress, above the title (sign-up). */
  readonly progress?: ReactNode;
  readonly children: ReactNode;
}

/** The nebula, loaded on first use in its own chunk: only the sign-in pages pay for it. */
const Nebula = lazy(() => import('@clavian/ui-web/nebula').then((module) => ({ default: module.Nebula })));

export function AuthLayout({ title, subtitle, footer, progress, children }: AuthLayoutProps): ReactNode {
  return (
    <main className="auth" id="console-main">
      <Suspense fallback={null}>
        <Nebula />
      </Suspense>
      <div className="auth__panel">
        <div className="auth__brand" aria-hidden="true">
          <Mark size={32} variant="eclipse" />
          <span className="auth__wordmark">
            Clavian<span className="console-wordmark__stop">.</span>
          </span>
        </div>
        <div className="auth__card">
          {progress === undefined ? null : <div className="auth__progress">{progress}</div>}
          <div className="auth__head">
            <h1>{title}</h1>
            {subtitle === undefined ? null : <p className="auth__subtitle console-muted">{subtitle}</p>}
          </div>
          {children}
        </div>
        {footer === undefined ? null : <p className="auth__footer console-subtle">{footer}</p>}
      </div>
    </main>
  );
}
